import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { ExportToCsv } from 'export-to-csv';
import { createMultiSectionPdfBlob } from './PDFExporter';

const CUSTOMER_SHEET_COLUMNS = [
    { fieldName: 'customer_name', headerName: 'Customer Name', width: 28 },
    { fieldName: 'project_name', headerName: 'Project Name', width: 22 },
    { fieldName: 'site_address', headerName: 'Site Address', width: 30 },
    { fieldName: 'contact_person', headerName: 'Contact Person', width: 18 },
    { fieldName: 'mobile_numbers', headerName: 'Mobile Nos', width: 18 },
    { fieldName: 'quotation_count', headerName: 'Quotations', width: 12 },
    { fieldName: 'sales_order_count', headerName: 'Sales Orders', width: 12 },
    { fieldName: 'delivery_challan_count', headerName: 'Delivery Challans', width: 14 },
    { fieldName: 'billing_name', headerName: 'Billing Name', width: 24 },
    { fieldName: 'billing_address', headerName: 'Address', width: 30 },
    { fieldName: 'state', headerName: 'State', width: 14 },
    { fieldName: 'pan_no', headerName: 'PAN No', width: 14 },
    { fieldName: 'gst_no', headerName: 'GST No', width: 18 },
    { fieldName: 'contact_details', headerName: 'Contact Details', width: 18 },
    { fieldName: 'email', headerName: 'Email', width: 24 },
    { fieldName: 'credit_limit', headerName: 'Credit Limit', width: 14 },
    { fieldName: 'customer_type', headerName: 'Type of Customer', width: 16 },
    { fieldName: 'nature_of_business', headerName: 'Nature of Business', width: 18 },
    { fieldName: 'marketing_name', headerName: 'Marketing Name', width: 18 },
];

const QUOTATION_SHEET_COLUMNS = [
    { fieldName: 'quotation_no', headerName: 'Quotation No', width: 18 },
    { fieldName: 'quotation_date', headerName: 'Date', width: 12 },
    { fieldName: 'customer_name', headerName: 'Customer', width: 24 },
    { fieldName: 'site_name', headerName: 'Site', width: 22 },
    { fieldName: 'grade', headerName: 'Grade', width: 10 },
    { fieldName: 'qty', headerName: 'Qty', width: 10 },
    { fieldName: 'rate', headerName: 'Rate', width: 12 },
    { fieldName: 'amount', headerName: 'Amount', width: 14 },
    { fieldName: 'delivery_mode', headerName: 'Mode of Delivery', width: 16 },
    { fieldName: 'distance', headerName: 'Distance', width: 10 },
    { fieldName: 'marketing_name', headerName: 'Marketing', width: 18 },
];

const SALES_ORDER_SHEET_COLUMNS = [
    { fieldName: 'sales_order_no', headerName: 'Sales Order No', width: 18 },
    { fieldName: 'sales_order_date', headerName: 'Order Date', width: 12 },
    { fieldName: 'customer_name', headerName: 'Customer', width: 24 },
    { fieldName: 'site_name', headerName: 'Site', width: 22 },
    { fieldName: 'grade', headerName: 'Grade', width: 12 },
    { fieldName: 'rate', headerName: 'Rate', width: 12 },
    { fieldName: 'qty', headerName: 'Qty', width: 10 },
    { fieldName: 'amount', headerName: 'Amount', width: 14 },
    { fieldName: 'transport_mode', headerName: 'Mode of Delivery', width: 16 },
    { fieldName: 'marketing_name', headerName: 'Marketing', width: 18 },
];

const DELIVERY_CHALLAN_SHEET_COLUMNS = [
    { fieldName: 'delivery_challan_no', headerName: 'DC No', width: 18 },
    { fieldName: 'delivery_challan_date', headerName: 'Date', width: 12 },
    { fieldName: 'customer_name', headerName: 'Customer', width: 24 },
    { fieldName: 'site_name', headerName: 'Site', width: 22 },
    { fieldName: 'grade', headerName: 'Grade', width: 10 },
    { fieldName: 'qty', headerName: 'Qty', width: 10 },
    { fieldName: 'rate', headerName: 'Rate', width: 12 },
    { fieldName: 'amount', headerName: 'Amount', width: 14 },
    { fieldName: 'delivery_mode', headerName: 'Mode of Delivery', width: 16 },
    { fieldName: 'distance', headerName: 'Distance', width: 10 },
    { fieldName: 'vehicle_name', headerName: 'Vehicle', width: 16 },
    { fieldName: 'driver_name', headerName: 'Driver', width: 16 },
    { fieldName: 'total_km', headerName: 'KM', width: 10 },
    { fieldName: 'marketing_name', headerName: 'Marketing', width: 18 },
];

function formatExportNumber(value, digits = 2) {
    const number = Number(value);
    if (!Number.isFinite(number)) {
        return '';
    }
    return Number(number.toFixed(digits));
}

function normalizeValue(value) {
    return value === null || value === undefined || value === 'undefined' || value === 'null' ? '' : value;
}

function mapRowsForSheet(columns, records, numericFields = []) {
    return records.map((record) => {
        const row = {};
        columns.forEach((col) => {
            let value = record[col.fieldName];
            if (numericFields.includes(col.fieldName) && value !== '' && value !== null && value !== undefined) {
                value = formatExportNumber(value, col.fieldName === 'qty' ? 3 : 2);
            }
            row[col.fieldName] = normalizeValue(value);
        });
        return row;
    });
}

function buildSheetTitleRows(meta) {
    return [
        ['Customer Data Report'],
        [`Period: ${meta.periodFrom || '-'} to ${meta.periodTo || '-'}`],
        [`Generated: ${meta.generatedAt || ''}`],
        [`Customer / Site Rows: ${meta.rowCount || 0}`],
    ];
}

function appendFormattedSheet(workbook, sheetName, titleRows, columns, records, numericFields = []) {
    const normalizedRecords = mapRowsForSheet(columns, records, numericFields);
    const headers = columns.map((col) => col.headerName);
    const dataRows = normalizedRecords.map((record) => columns.map((col) => record[col.fieldName]));
    const worksheet = XLSX.utils.aoa_to_sheet([...titleRows, [], headers, ...dataRows]);
    worksheet['!cols'] = columns.map((col) => ({ wch: col.width || 15 }));
    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
}

function sumField(records, field) {
    return records.reduce((sum, row) => sum + (Number(row[field]) || 0), 0);
}

function buildFooterRow(columns, totalsByField) {
    const footer = {};
    columns.forEach((col, index) => {
        if (index === 0) {
            footer[col.fieldName] = 'TOTAL';
        } else if (Object.prototype.hasOwnProperty.call(totalsByField, col.fieldName)) {
            footer[col.fieldName] = totalsByField[col.fieldName];
        } else {
            footer[col.fieldName] = '';
        }
    });
    return footer;
}

export function exportCustomerDataReportExcel(exportData, filename, meta) {
    const workbook = XLSX.utils.book_new();
    const titleRows = buildSheetTitleRows(meta);
    const customerRows = exportData.customer_data_report_list || [];

    appendFormattedSheet(
        workbook,
        'Customer Data',
        titleRows,
        CUSTOMER_SHEET_COLUMNS,
        customerRows,
        ['credit_limit', 'quotation_count', 'sales_order_count', 'delivery_challan_count'],
    );

    const quotationRows = exportData.quotation_export_list || [];
    if (quotationRows.length) {
        appendFormattedSheet(
            workbook,
            'Quotations',
            titleRows,
            QUOTATION_SHEET_COLUMNS,
            quotationRows,
            ['qty', 'rate', 'amount', 'distance'],
        );
    }

    const salesOrderRows = exportData.sales_order_export_list || [];
    if (salesOrderRows.length) {
        appendFormattedSheet(
            workbook,
            'Sales Orders',
            titleRows,
            SALES_ORDER_SHEET_COLUMNS,
            salesOrderRows,
            ['qty', 'rate', 'amount'],
        );
    }

    const deliveryChallanRows = exportData.delivery_challan_export_list || [];
    if (deliveryChallanRows.length) {
        appendFormattedSheet(
            workbook,
            'Delivery Challans',
            titleRows,
            DELIVERY_CHALLAN_SHEET_COLUMNS,
            deliveryChallanRows,
            ['qty', 'rate', 'amount', 'distance', 'total_km'],
        );
    }

    const buffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    saveAs(
        new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
        filename.endsWith('.xlsx') ? filename : `${filename}.xlsx`,
    );
}

function toPdfColumns(columns) {
    return columns.map((col) => ({
        header: col.headerName,
        dataKey: col.fieldName,
        align: ['qty', 'rate', 'amount', 'distance', 'total_km', 'credit_limit', 'quotation_count', 'sales_order_count', 'delivery_challan_count'].includes(col.fieldName)
            ? 'right'
            : undefined,
    }));
}

export function createCustomerDataReportPdfBlob(exportData, header, meta) {
    const summaryLines = [
        `Period: ${meta.periodFrom || '-'} to ${meta.periodTo || '-'}`,
        `Generated: ${meta.generatedAt || ''}`,
        `Customer / Site Rows: ${meta.rowCount || 0}`,
    ];

    const sections = [
        {
            title: 'Customer / Site Summary',
            columns: toPdfColumns(CUSTOMER_SHEET_COLUMNS),
            records: mapRowsForSheet(
                CUSTOMER_SHEET_COLUMNS,
                exportData.customer_data_report_list || [],
                ['credit_limit', 'quotation_count', 'sales_order_count', 'delivery_challan_count'],
            ),
        },
    ];

    const quotationRows = exportData.quotation_export_list || [];
    if (quotationRows.length) {
        sections.push({
            title: `Quotations (${quotationRows.length})`,
            columns: toPdfColumns(QUOTATION_SHEET_COLUMNS),
            records: mapRowsForSheet(QUOTATION_SHEET_COLUMNS, quotationRows, ['qty', 'rate', 'amount', 'distance']),
            footerRow: buildFooterRow(QUOTATION_SHEET_COLUMNS, {
                qty: formatExportNumber(sumField(quotationRows, 'qty'), 3),
                amount: formatExportNumber(sumField(quotationRows, 'amount'), 2),
            }),
        });
    }

    const salesOrderRows = exportData.sales_order_export_list || [];
    if (salesOrderRows.length) {
        sections.push({
            title: `Sales Orders (${salesOrderRows.length})`,
            columns: toPdfColumns(SALES_ORDER_SHEET_COLUMNS),
            records: mapRowsForSheet(SALES_ORDER_SHEET_COLUMNS, salesOrderRows, ['qty', 'rate', 'amount']),
            footerRow: buildFooterRow(SALES_ORDER_SHEET_COLUMNS, {
                qty: formatExportNumber(sumField(salesOrderRows, 'qty'), 3),
                amount: formatExportNumber(sumField(salesOrderRows, 'amount'), 2),
            }),
        });
    }

    const deliveryChallanRows = exportData.delivery_challan_export_list || [];
    if (deliveryChallanRows.length) {
        sections.push({
            title: `Delivery Challans (${deliveryChallanRows.length})`,
            columns: toPdfColumns(DELIVERY_CHALLAN_SHEET_COLUMNS),
            records: mapRowsForSheet(DELIVERY_CHALLAN_SHEET_COLUMNS, deliveryChallanRows, ['qty', 'rate', 'amount', 'distance', 'total_km']),
            footerRow: buildFooterRow(DELIVERY_CHALLAN_SHEET_COLUMNS, {
                qty: formatExportNumber(sumField(deliveryChallanRows, 'qty'), 3),
                amount: formatExportNumber(sumField(deliveryChallanRows, 'amount'), 2),
                total_km: formatExportNumber(sumField(deliveryChallanRows, 'total_km'), 3),
            }),
        });
    }

    return createMultiSectionPdfBlob({ header, summaryLines, sections });
}

function popupColumnsToSheetColumns(columns) {
    return columns.map((col) => ({
        fieldName: col.key,
        headerName: col.label,
        width: Math.max(12, Math.min(24, String(col.label || '').length + 4)),
    }));
}

function buildPopupTitleRows({ title, subtitle, periodFrom, periodTo, generatedAt }) {
    return [
        [title || 'Details'],
        [subtitle || ''],
        [`Period: ${periodFrom || '-'} to ${periodTo || '-'}`],
        [`Generated: ${generatedAt || ''}`],
    ];
}

function recordsFromPopupRows(columns, records) {
    const sheetColumns = popupColumnsToSheetColumns(columns);
    return records.map((record) => {
        const row = {};
        sheetColumns.forEach((col) => {
            row[col.fieldName] = normalizeValue(record[col.fieldName]);
        });
        return row;
    });
}

function footerFromPopupRow(columns, footerRow) {
    if (!footerRow) {
        return null;
    }
    const mapped = {};
    columns.forEach((col) => {
        mapped[col.key] = normalizeValue(footerRow[col.key]);
    });
    return mapped;
}

export function exportTransactionDetailExcel({
    title,
    subtitle,
    columns,
    records,
    footerRow,
    filename,
    periodFrom,
    periodTo,
    generatedAt,
}) {
    const sheetColumns = popupColumnsToSheetColumns(columns);
    const titleRows = buildPopupTitleRows({ title, subtitle, periodFrom, periodTo, generatedAt });
    const headers = sheetColumns.map((col) => col.headerName);
    const dataRows = recordsFromPopupRows(columns, records).map(
        (record) => sheetColumns.map((col) => record[col.fieldName]),
    );
    const footer = footerFromPopupRow(columns, footerRow);
    const footerRows = footer
        ? [sheetColumns.map((col) => footer[col.fieldName] ?? '')]
        : [];

    const worksheet = XLSX.utils.aoa_to_sheet([...titleRows, [], headers, ...dataRows, ...footerRows]);
    worksheet['!cols'] = sheetColumns.map((col) => ({ wch: col.width || 15 }));
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Details');
    const buffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const safeName = filename.endsWith('.xlsx') ? filename : `${filename}.xlsx`;
    saveAs(
        new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
        safeName,
    );
}

export function exportTransactionDetailCsv({
    columns,
    records,
    footerRow,
    filename,
}) {
    const sheetColumns = popupColumnsToSheetColumns(columns);
    const exportRecords = recordsFromPopupRows(columns, records);
    const footer = footerFromPopupRow(columns, footerRow);
    if (footer) {
        exportRecords.push(footer);
    }

    const headers = sheetColumns.map((col) => col.headerName);
    const csvOptions = {
        fieldSeparator: ',',
        quoteStrings: '"',
        decimalSeparator: '.',
        showLabels: true,
        useBom: true,
        useKeysAsHeaders: false,
        headers,
        filename: filename.endsWith('.csv') ? filename.replace(/\.csv$/i, '') : filename,
    };
    const rows = exportRecords.map((record) => {
        const csvRow = {};
        sheetColumns.forEach((col) => {
            csvRow[col.headerName] = record[col.fieldName] ?? '';
        });
        return csvRow;
    });

    new ExportToCsv(csvOptions).generateCsv(rows);
}

export function createTransactionDetailPdfBlob({
    title,
    subtitle,
    columns,
    records,
    footerRow,
    periodFrom,
    periodTo,
    generatedAt,
    header = null,
}) {
    const sheetColumns = popupColumnsToSheetColumns(columns);
    const mappedRecords = recordsFromPopupRows(columns, records);
    const mappedFooter = footerFromPopupRow(columns, footerRow);
    const pdfFooter = mappedFooter
        ? sheetColumns.reduce((acc, col) => {
            acc[col.fieldName] = mappedFooter[col.fieldName] ?? '';
            return acc;
        }, {})
        : null;

    return createMultiSectionPdfBlob({
        header,
        summaryLines: [
            subtitle || '',
            `Period: ${periodFrom || '-'} to ${periodTo || '-'}`,
            `Generated: ${generatedAt || ''}`,
        ].filter(Boolean),
        sections: [{
            title: title || 'Details',
            columns: toPdfColumns(sheetColumns),
            records: mappedRecords,
            footerRow: pdfFooter,
        }],
    });
}
